import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  DataTable,
  Text,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

function formatCurrency(dollars: number): string {
  return `$${Number(dollars).toFixed(2)}`;
}

export default function PatientReport() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'patient', startDate, endDate],
    queryFn: () => reportsApi.getPatient({ startDate, endDate }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/patient', { startDate, endDate });
    } finally {
      setExporting(false);
    }
  };

  const rows = (data?.data || []).map((p: any) => [
    p.name || '\u2014',
    p.email || '\u2014',
    String(p.totalRentals),
    formatCurrency(p.totalRevenue),
    (p.recentRentals || [])
      .map((r: any) => r.orderNumber || r.id?.slice(0, 8))
      .join(', ') || '\u2014',
  ]);

  return (
    <Page
      title="Patient Report"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the Patient report. Try refreshing.</p>
            </EmptyState>
          ) : !data?.data?.length ? (
            <EmptyState heading="No data" image="">
              <p>No patient data found for the selected date range.</p>
            </EmptyState>
          ) : (
            <BlockStack gap="400">
              <Text as="p" variant="bodyMd" tone="subdued">
                {data.data.length} patients with rentals in this period
              </Text>
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'text', 'text']}
                headings={['Patient Name', 'Email', 'Total Rentals', 'Total Revenue', 'Recent Orders']}
                rows={rows}
                footerContent={rows.length === 0 ? 'No records found' : undefined}
              />
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
